import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, ShieldCheck, RefreshCw, CheckCircle2, XCircle, Globe } from "lucide-react";
import { productAssessmentsApi, enrichmentApi, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FIT_META = {
  STRONG: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Strong fit" },
  GOOD: { cls: "bg-teal-50 text-teal-700 border-teal-200", label: "Good fit" },
  POSSIBLE: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Possible fit" },
  WEAK: { cls: "bg-orange-50 text-orange-700 border-orange-200", label: "Weak fit" },
  NOT_ENOUGH_EVIDENCE: { cls: "bg-slate-100 text-slate-500 border-slate-200", label: "Not enough evidence" },
  NOT_RELEVANT: { cls: "bg-slate-50 text-slate-400 border-slate-200", label: "Not relevant" },
  NOT_APPLICABLE: { cls: "bg-sky-50 text-sky-600 border-sky-200", label: "Not applicable" },
};

const PRODUCT_NAME = { CATERCTRL: "CaterCTRL", VOWGENDA: "Vowgenda", CENAMOS: "Cenamos", HOSTRELAY: "HostRelay" };
const prettyFactor = (f) => f.replace(/_/g, " ").replace(/:/g, ": ");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—");

export function ProductOpportunities({ orgId }) {
  const { isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [enriching, setEnriching] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await productAssessmentsApi.list(orgId);
      setItems(data.items);
    } catch (e) { /* silent — section is additive */ } finally { setLoading(false); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const runEnrichment = async () => {
    setEnriching(true);
    try {
      const { data } = await enrichmentApi.run(orgId);
      const n = (data.facts || []).length;
      toast.success(data.run?.status === "success" ? `Enriched — ${n} capability fact(s) found` : `Enrichment ${data.run?.status}: ${data.run?.summary || (data.run?.errors || []).join(", ")}`);
      load();
    } catch (e) {
      const code = e?.response?.data?.error?.code;
      toast.error(code === "RATE_LIMITED" ? "Website rate limit hit — try again shortly" : apiErrorMessage(e));
    } finally { setEnriching(false); }
  };

  const setVerified = async (code, verified) => {
    setBusy(code);
    try {
      await (verified ? productAssessmentsApi.verify(orgId, code) : productAssessmentsApi.unverify(orgId, code));
      toast.success(verified ? "Assessment verified" : "Verification removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(null); }
  };

  if (loading) return <div className="mt-6 h-24 animate-pulse rounded-xl border border-slate-200 bg-white" />;
  if (items.length === 0) return null;

  return (
    <section className="mt-6" data-testid="product-opportunities">
      <div className="mb-2.5 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-teal-600" />
        <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Product Opportunities</h2>
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-300">Deterministic · rules {items[0]?.ruleset_version || "v1"}</span>
        {isManager && (
          <button data-testid="enrich-btn" onClick={runEnrichment} disabled={enriching}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-[11px] font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-60">
            <Globe className={`h-3 w-3 ${enriching ? "animate-pulse" : ""}`} /> {enriching ? "Enriching…" : "Enrich from website"}
          </button>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((a) => {
          const m = FIT_META[a.fit_class] || FIT_META.NOT_ENOUGH_EVIDENCE;
          const gated = a.fit_class === "NOT_APPLICABLE";
          const reasons = [...(a.contributing_factors || []), ...(a.supporting_evidence || [])].slice(0, 3);
          return (
            <div key={a.product_code} data-testid={`assessment-${a.product_code}`} className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold text-slate-800">{PRODUCT_NAME[a.product_code] || a.product_code}</p>
                  <span data-testid={`fit-${a.product_code}`} className={`mt-1 inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>
                </div>
                {a.verified && (
                  <span data-testid={`verified-${a.product_code}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                    <ShieldCheck className="h-3 w-3" /> Verified
                  </span>
                )}
              </div>

              {reasons.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-1 text-[11px] text-slate-500">
                      <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-teal-400" /> {prettyFactor(r)}
                    </li>
                  ))}
                </ul>
              )}
              {gated ? (
                <div data-testid={`gated-banner-${a.product_code}`} className="mt-2 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[11px] font-medium text-sky-700">
                  HostRelay unlocks once Vowgenda or Cenamos is in play
                </div>
              ) : (
                <>
                  {a.contradictory_evidence?.length > 0 && (
                    <p className="mt-1.5 text-[11px] italic text-amber-600">{a.contradictory_evidence[0]}</p>
                  )}

                  <div className="mt-2.5 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wide text-slate-400">Confidence {(a.confidence * 100).toFixed(0)}% · {fmtDate(a.evaluated_at)}</span>
                    {isManager && (
                      a.verified ? (
                        <button data-testid={`unverify-${a.product_code}`} disabled={busy === a.product_code} onClick={() => setVerified(a.product_code, false)}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-50 disabled:opacity-60"><XCircle className="h-3 w-3" /> Unverify</button>
                      ) : (
                        <button data-testid={`verify-${a.product_code}`} disabled={busy === a.product_code} onClick={() => setVerified(a.product_code, true)}
                          className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-[11px] font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-60"><CheckCircle2 className="h-3 w-3" /> Verify</button>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
